/**
 * Admin — Access & Benefits.
 *
 * One place to see and operate every reason someone pays less: coupons,
 * scholarships, partners, institutions, invitations, referral rewards, usage
 * analytics and fraud alerts. Read-only where the existing Coupons screen
 * already owns editing; operational where the benefits layer is new.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PageHead from "@/components/PageHead";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BENEFIT_TYPE_RULES,
  BENEFIT_POLICY_SUMMARY,
  BENEFIT_VERSION,
  INVITATION_BATCH_SIZES,
  INVITATION_PREFIXES,
  MIGRATING_CODES,
  type BenefitType,
} from "@/pricing/access-benefits";
import { PRICING_VERSION } from "@/pricing";
import {
  AlertTriangle,
  ArrowLeft,
  Download,
  Gift,
  GraduationCap,
  Landmark,
  RefreshCw,
  Ticket,
  Users,
} from "lucide-react";

type Analytics = {
  since?: string;
  byBenefitType?: Array<{
    benefit_type: string;
    redemptions: number;
    discount: number;
    revenue: number;
  }>;
  topCoupons?: Array<{
    code: string;
    label: string;
    redemption_count: number;
    max_redemptions: number | null;
    benefit_type: string;
  }>;
  invitations?: { total?: number; active?: number; used?: number; revoked?: number };
  grants?: Array<{ benefit_type: string; status: string; total: number }>;
  scholarships?: Array<{ status: string; total: number }>;
  fraudAlerts?: Array<{
    ip_hash: string;
    accounts: number;
    redemptions: number;
    last_seen: string;
  }>;
  auditRecent?: Array<{
    created_at: string;
    benefit_type: string | null;
    benefit_id: string | null;
    coupon_code: string | null;
    decision: string;
    currency: string | null;
    discount: number | null;
    final_amount: number | null;
    gateway: string | null;
    pricing_version: string | null;
  }>;
};

type InviteRow = {
  id: string;
  code: string;
  benefit_type: string;
  benefit_id: string;
  assigned_email: string | null;
  max_uses: number;
  uses: number;
  status: string;
  expires_at: string | null;
  notes: string | null;
  created_at: string;
};

type GrantRow = {
  id: string;
  user_id: string;
  benefit_type: string;
  benefit_id: string;
  status: string;
  partner_id: string | null;
  institution_id: string | null;
  source: string;
  valid_until: string | null;
  created_at: string;
};

type ApplicationRow = {
  id: string;
  user_id: string;
  status: string;
  requested_benefit_id: string;
  institution_name: string | null;
  created_at: string;
  decision_notes: string | null;
};

const fmtDate = (s?: string | null) =>
  s ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

const money = (amount?: number | null, currency = "INR") =>
  new Intl.NumberFormat(currency === "INR" ? "en-IN" : "en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(Number(amount ?? 0));

const statusVariant = (s: string) =>
  s === "active" || s === "approved"
    ? ("default" as const)
    : s === "revoked" || s === "rejected"
      ? ("destructive" as const)
      : ("secondary" as const);

function downloadCsv(filename: string, rows: Array<Record<string, unknown>>) {
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const escape = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
  ].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const BENEFIT_TYPES = Object.keys(BENEFIT_TYPE_RULES) as BenefitType[];

const AdminAccessBenefits = () => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [grants, setGrants] = useState<GrantRow[]>([]);
  const [applications, setApplications] = useState<ApplicationRow[]>([]);

  // Invitation generator
  const [count, setCount] = useState<number>(10);
  const [prefix, setPrefix] = useState<string>("FNF");
  const [benefitId, setBenefitId] = useState<string>("VKITFNF");
  const [benefitType, setBenefitType] = useState<BenefitType>("PRIVATE_INVITATION");
  const [expiresAt, setExpiresAt] = useState<string>("");
  const [assignedEmail, setAssignedEmail] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, i, g, s] = await Promise.all([
        supabase.rpc("get_benefit_analytics", { p_days: 90 }),
        supabase
          .from("invitation_codes")
          .select("*")
          .order("created_at", { ascending: false })
          .limit(300),
        supabase
          .from("benefit_grants")
          .select("*")
          .order("created_at", { ascending: false })
          .limit(300),
        supabase
          .from("scholarship_applications")
          .select("id,user_id,status,requested_benefit_id,institution_name,created_at,decision_notes")
          .order("created_at", { ascending: false })
          .limit(200),
      ]);
      if (a.error) throw a.error;
      setAnalytics((a.data ?? null) as Analytics | null);
      setInvites((i.data ?? []) as InviteRow[]);
      setGrants((g.data ?? []) as GrantRow[]);
      setApplications((s.data ?? []) as ApplicationRow[]);
    } catch (e) {
      toast.error("Couldn't load benefits data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data, error } = await supabase.rpc("generate_invitation_codes", {
        p_count: count,
        p_benefit_id: benefitId.trim().toUpperCase(),
        p_prefix: prefix,
        p_benefit_type: benefitType,
        p_expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        p_assigned_email: assignedEmail.trim() || null,
        p_max_uses: 1,
        p_notes: notes.trim() || null,
      });
      if (error) throw error;
      const rows = (data ?? []) as Array<{ code: string; batch_id: string; expires_at: string | null }>;
      toast.success(`Generated ${rows.length} invitation code${rows.length === 1 ? "" : "s"}.`);
      downloadCsv(
        `invitations-${benefitId}-${new Date().toISOString().slice(0, 10)}.csv`,
        rows.map((r) => ({
          code: r.code,
          benefit: benefitId.toUpperCase(),
          benefit_type: benefitType,
          assigned_email: assignedEmail.trim() || "",
          expires_at: r.expires_at ?? "",
          notes: notes.trim() || "",
        })),
      );
      await load();
    } catch (e) {
      toast.error((e as Error).message || "Couldn't generate codes.");
    } finally {
      setGenerating(false);
    }
  };

  const revokeInvite = async (code: string) => {
    const { error } = await supabase.rpc("revoke_invitation_code", { p_code: code });
    if (error) return toast.error("Couldn't revoke that code.");
    toast.success(`${code} revoked.`);
    await load();
  };

  const revokeGrant = async (id: string) => {
    const { error } = await supabase.rpc("revoke_benefit", { p_grant_id: id });
    if (error) return toast.error("Couldn't revoke that benefit.");
    toast.success("Benefit revoked.");
    await load();
  };

  const decide = async (id: string, approve: boolean) => {
    const { error } = await supabase.rpc("decide_scholarship_application", {
      p_application_id: id,
      p_approve: approve,
      p_notes: null,
    });
    if (error) return toast.error("Couldn't record that decision.");
    toast.success(approve ? "Scholarship approved and granted." : "Application rejected.");
    await load();
  };

  const revenueByBenefit = useMemo(
    () => analytics?.byBenefitType ?? [],
    [analytics],
  );

  const partnerGrants = grants.filter((g) => g.benefit_type === "PARTNER");
  const institutionGrants = grants.filter((g) => g.benefit_type === "INSTITUTION");
  const referralGrants = grants.filter(
    (g) => g.benefit_type === "REFERRAL" || g.benefit_type === "AMBASSADOR",
  );

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <PageHead
        title="Access & Benefits — WeKIT Admin"
        description="Operate coupons, scholarships, partner and institution pricing, invitations, referral rewards, usage analytics and fraud alerts."
        path="/admin/access-benefits"
        noIndex
      />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <Button asChild variant="ghost" size="sm" className="mb-2 -ml-2">
            <Link to="/admin">
              <ArrowLeft className="h-4 w-4 mr-1" /> Admin hub
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">Access &amp; Benefits</h1>
          <p className="text-sm text-muted-foreground">
            Benefits Engine v{BENEFIT_VERSION} · Pricing Policy v{PRICING_VERSION} · one benefit per
            order, stacking disabled.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <Tabs defaultValue="dashboard">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="coupons">Coupons</TabsTrigger>
          <TabsTrigger value="scholarships">Scholarships</TabsTrigger>
          <TabsTrigger value="partners">Partners</TabsTrigger>
          <TabsTrigger value="institutions">Institutions</TabsTrigger>
          <TabsTrigger value="invitations">Invitations</TabsTrigger>
          <TabsTrigger value="referrals">Referral rewards</TabsTrigger>
          <TabsTrigger value="analytics">Usage analytics</TabsTrigger>
          <TabsTrigger value="fraud">Fraud alerts</TabsTrigger>
        </TabsList>

        {/* ---------------- Dashboard ---------------- */}
        <TabsContent value="dashboard" className="space-y-4 pt-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Ticket className="h-4 w-4" /> Invitations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold">{analytics?.invitations?.total ?? 0}</p>
                <p className="text-xs text-muted-foreground">
                  {analytics?.invitations?.active ?? 0} active · {analytics?.invitations?.used ?? 0}{" "}
                  used · {analytics?.invitations?.revoked ?? 0} revoked
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <GraduationCap className="h-4 w-4" /> Scholarships
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold">
                  {applications.filter((a) => a.status === "submitted").length}
                </p>
                <p className="text-xs text-muted-foreground">awaiting review</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Users className="h-4 w-4" /> Entitlements
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold">
                  {grants.filter((g) => g.status === "active").length}
                </p>
                <p className="text-xs text-muted-foreground">active account grants</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" /> Fraud alerts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold">{analytics?.fraudAlerts?.length ?? 0}</p>
                <p className="text-xs text-muted-foreground">networks with shared redemptions</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Benefit policy</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-5">
                {BENEFIT_POLICY_SUMMARY.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Benefit types</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Layer</TableHead>
                    <TableHead>Claimed via</TableHead>
                    <TableHead>Per account</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>What it is</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {BENEFIT_TYPES.map((t) => {
                    const r = BENEFIT_TYPE_RULES[t];
                    return (
                      <TableRow key={t}>
                        <TableCell className="font-medium">{r.label}</TableCell>
                        <TableCell>{r.layer}</TableCell>
                        <TableCell>{r.claimMode}</TableCell>
                        <TableCell>{r.maxUsesPerUser}</TableCell>
                        <TableCell>{r.priority}</TableCell>
                        <TableCell className="text-muted-foreground text-xs max-w-md">
                          {r.description}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Migration to account-based benefits</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code (still working)</TableHead>
                    <TableHead>Becomes</TableHead>
                    <TableHead>Route</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {MIGRATING_CODES.map((m) => (
                    <TableRow key={m.code}>
                      <TableCell className="font-mono">{m.code}</TableCell>
                      <TableCell>{m.to}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{m.route}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------------- Coupons ---------------- */}
        <TabsContent value="coupons" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Top coupons</CardTitle>
              <Button asChild variant="outline" size="sm">
                <Link to="/admin/coupons">Manage coupons</Link>
              </Button>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Benefit type</TableHead>
                    <TableHead>Redemptions</TableHead>
                    <TableHead>Cap</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(analytics?.topCoupons ?? []).map((c) => (
                    <TableRow key={c.code}>
                      <TableCell className="font-mono">{c.code}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{c.benefit_type}</Badge>
                      </TableCell>
                      <TableCell>{c.redemption_count ?? 0}</TableCell>
                      <TableCell>{c.max_redemptions ?? "unlimited"}</TableCell>
                    </TableRow>
                  ))}
                  {(analytics?.topCoupons ?? []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-muted-foreground">
                        No coupons yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------------- Scholarships ---------------- */}
        <TabsContent value="scholarships" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Applications</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Learner</TableHead>
                    <TableHead>Requested</TableHead>
                    <TableHead>Institution</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Decision</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applications.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell className="text-xs">{fmtDate(a.created_at)}</TableCell>
                      <TableCell className="font-mono text-xs">{a.user_id.slice(0, 8)}…</TableCell>
                      <TableCell className="font-mono text-xs">{a.requested_benefit_id}</TableCell>
                      <TableCell className="text-xs">{a.institution_name ?? "—"}</TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(a.status)}>{a.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        {a.status === "submitted" ? (
                          <>
                            <Button size="sm" onClick={() => decide(a.id, true)}>
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => decide(a.id, false)}
                            >
                              Reject
                            </Button>
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {applications.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-muted-foreground">
                        No applications yet. Legacy scholarship codes keep working meanwhile.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------------- Partners / Institutions / Referrals ---------------- */}
        {[
          { key: "partners", label: "Partner entitlements", rows: partnerGrants, icon: Users },
          {
            key: "institutions",
            label: "Institution entitlements",
            rows: institutionGrants,
            icon: Landmark,
          },
          { key: "referrals", label: "Referral & ambassador rewards", rows: referralGrants, icon: Gift },
        ].map((section) => (
          <TabsContent key={section.key} value={section.key} className="space-y-4 pt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <section.icon className="h-4 w-4" /> {section.label}
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Granted</TableHead>
                      <TableHead>Learner</TableHead>
                      <TableHead>Benefit</TableHead>
                      <TableHead>Partner / Institution</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {section.rows.map((g) => (
                      <TableRow key={g.id}>
                        <TableCell className="text-xs">{fmtDate(g.created_at)}</TableCell>
                        <TableCell className="font-mono text-xs">{g.user_id.slice(0, 8)}…</TableCell>
                        <TableCell className="font-mono text-xs">{g.benefit_id}</TableCell>
                        <TableCell className="text-xs">
                          {g.partner_id ?? g.institution_id ?? "—"}
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusVariant(g.status)}>{g.status}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {g.status === "active" && (
                            <Button size="sm" variant="outline" onClick={() => revokeGrant(g.id)}>
                              Revoke
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {section.rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-muted-foreground">
                          Nothing granted yet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        ))}

        {/* ---------------- Invitations ---------------- */}
        <TabsContent value="invitations" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Generate invitation codes</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-3">
              <div>
                <Label htmlFor="count">How many</Label>
                <Select value={String(count)} onValueChange={(v) => setCount(Number(v))}>
                  <SelectTrigger id="count">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INVITATION_BATCH_SIZES.map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="prefix">Prefix</Label>
                <Select value={prefix} onValueChange={setPrefix}>
                  <SelectTrigger id="prefix">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(INVITATION_PREFIXES).map(([k, v]) => (
                      <SelectItem key={v} value={v}>
                        {v} — {k.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="benefitType">Benefit type</Label>
                <Select
                  value={benefitType}
                  onValueChange={(v) => setBenefitType(v as BenefitType)}
                >
                  <SelectTrigger id="benefitType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BENEFIT_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {BENEFIT_TYPE_RULES[t].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="benefitId">Benefit (priced rule id)</Label>
                <Input
                  id="benefitId"
                  value={benefitId}
                  onChange={(e) => setBenefitId(e.target.value.toUpperCase())}
                  placeholder="VKITFNF"
                />
              </div>
              <div>
                <Label htmlFor="expires">Expires</Label>
                <Input
                  id="expires"
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="email">Assign to email (optional)</Label>
                <Input
                  id="email"
                  type="email"
                  value={assignedEmail}
                  onChange={(e) => setAssignedEmail(e.target.value)}
                  placeholder="name@example.com"
                />
              </div>
              <div className="md:col-span-3">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={500}
                  rows={2}
                />
              </div>
              <div className="md:col-span-3 flex flex-wrap gap-2">
                <Button onClick={generate} disabled={generating || !benefitId.trim()}>
                  {generating ? "Generating…" : `Generate ${count} codes`}
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    downloadCsv(
                      "invitation-codes.csv",
                      invites.map((i) => ({
                        code: i.code,
                        benefit_type: i.benefit_type,
                        benefit: i.benefit_id,
                        assigned_email: i.assigned_email ?? "",
                        status: i.status,
                        uses: i.uses,
                        max_uses: i.max_uses,
                        expires_at: i.expires_at ?? "",
                        notes: i.notes ?? "",
                      })),
                    )
                  }
                  disabled={invites.length === 0}
                >
                  <Download className="h-4 w-4 mr-2" /> Export CSV
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Issued codes</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Benefit</TableHead>
                    <TableHead>Assigned to</TableHead>
                    <TableHead>Uses</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invites.map((i) => (
                    <TableRow key={i.id}>
                      <TableCell className="font-mono">{i.code}</TableCell>
                      <TableCell className="text-xs">
                        {i.benefit_id}
                        <Badge variant="outline" className="ml-2">
                          {i.benefit_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{i.assigned_email ?? "unassigned"}</TableCell>
                      <TableCell className="text-xs">
                        {i.uses}/{i.max_uses}
                      </TableCell>
                      <TableCell className="text-xs">{fmtDate(i.expires_at)}</TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(i.status)}>{i.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {i.status === "active" && (
                          <Button size="sm" variant="outline" onClick={() => revokeInvite(i.code)}>
                            Revoke
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {invites.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-muted-foreground">
                        No invitation codes yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------------- Usage analytics ---------------- */}
        <TabsContent value="analytics" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Revenue by benefit</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Benefit type</TableHead>
                    <TableHead>Redemptions</TableHead>
                    <TableHead>Discount given</TableHead>
                    <TableHead>Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {revenueByBenefit.map((r) => (
                    <TableRow key={r.benefit_type}>
                      <TableCell>{r.benefit_type}</TableCell>
                      <TableCell>{r.redemptions}</TableCell>
                      <TableCell>{money(r.discount)}</TableCell>
                      <TableCell>{money(r.revenue)}</TableCell>
                    </TableRow>
                  ))}
                  {revenueByBenefit.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-muted-foreground">
                        No benefit redemptions recorded yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Benefit audit log (latest 50)</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadCsv("benefit-audit.csv", analytics?.auditRecent ?? [])}
                disabled={(analytics?.auditRecent ?? []).length === 0}
              >
                <Download className="h-4 w-4 mr-2" /> Export
              </Button>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Decision</TableHead>
                    <TableHead>Benefit</TableHead>
                    <TableHead>Coupon</TableHead>
                    <TableHead>Discount</TableHead>
                    <TableHead>Final</TableHead>
                    <TableHead>Gateway</TableHead>
                    <TableHead>Pricing</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(analytics?.auditRecent ?? []).map((r, idx) => (
                    <TableRow key={`${r.created_at}-${idx}`}>
                      <TableCell className="text-xs">{fmtDate(r.created_at)}</TableCell>
                      <TableCell>
                        <Badge variant={r.decision === "applied" ? "default" : "secondary"}>
                          {r.decision}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.benefit_id ?? "—"}{" "}
                        {r.benefit_type && (
                          <Badge variant="outline" className="ml-1">
                            {r.benefit_type}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{r.coupon_code ?? "—"}</TableCell>
                      <TableCell className="text-xs">
                        {money(r.discount, r.currency ?? "INR")}
                      </TableCell>
                      <TableCell className="text-xs">
                        {money(r.final_amount, r.currency ?? "INR")}
                      </TableCell>
                      <TableCell className="text-xs">{r.gateway ?? "—"}</TableCell>
                      <TableCell className="text-xs">{r.pricing_version ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                  {(analytics?.auditRecent ?? []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-muted-foreground">
                        No benefit decisions recorded yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------------- Fraud ---------------- */}
        <TabsContent value="fraud" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Fraud alerts</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Network (hashed)</TableHead>
                    <TableHead>Accounts</TableHead>
                    <TableHead>Redemptions</TableHead>
                    <TableHead>Last seen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(analytics?.fraudAlerts ?? []).map((f) => (
                    <TableRow key={f.ip_hash}>
                      <TableCell className="font-mono text-xs">{f.ip_hash.slice(0, 12)}…</TableCell>
                      <TableCell>{f.accounts}</TableCell>
                      <TableCell>{f.redemptions}</TableCell>
                      <TableCell className="text-xs">{fmtDate(f.last_seen)}</TableCell>
                    </TableRow>
                  ))}
                  {(analytics?.fraudAlerts ?? []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-muted-foreground">
                        Nothing suspicious. Duplicate redemptions are blocked at source.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminAccessBenefits;
